import { useEffect, useRef } from "react";
import { Bell, X } from "lucide-react";
import { notify } from "@/lib/notify";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAuthStore } from "@/store/auth.store";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const PROMPT_DELAY_MS = 4_000;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // stockage bloqué : la bannière réapparaîtra, tant pis
  }
}

function recentlyDismissed(): boolean {
  const dismissed = safeGet(STORAGE_KEYS.PUSH_PROMPT_DISMISSED_AT);
  if (dismissed === "never") return true;
  if (!dismissed) return false;
  const at = Number(dismissed);
  return !Number.isNaN(at) && Date.now() - at < SEVEN_DAYS_MS;
}

function PromptToastBody({
  toastId,
  onAccept,
}: {
  toastId: string | number;
  onAccept: () => void;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-2xl w-[340px] bg-[#003366] border border-white/10">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 text-fuchsia-300 bg-fuchsia-400/20">
        <Bell size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-black text-sm leading-tight">Activer les notifications ?</p>
        <p className="text-white/60 text-xs font-medium mt-0.5 leading-snug">
          Soyez alerté des nouveaux messages, devoirs et sessions live.
        </p>
        <div className="flex gap-2 mt-2.5">
          <button
            onClick={onAccept}
            className="px-3 py-1.5 rounded-lg bg-[#c026d3] hover:bg-[#a21caf] text-white text-xs font-bold transition-colors cursor-pointer"
          >
            Activer
          </button>
          <button
            onClick={() => {
              safeSet(STORAGE_KEYS.PUSH_PROMPT_DISMISSED_AT, String(Date.now()));
              notify.dismiss(toastId);
            }}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-xs font-bold transition-colors cursor-pointer"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button
        onClick={() => {
          safeSet(STORAGE_KEYS.PUSH_PROMPT_DISMISSED_AT, String(Date.now()));
          notify.dismiss(toastId);
        }}
        className="shrink-0 mt-0.5 p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}

/**
 * Invite non-intrusive à activer les notifications push, affichée une fois par session après
 * connexion — jamais au chargement brut de la page (cf. §3.2 point 3 de
 * `docs/PWA_PUSH_NOTIFICATIONS.md` : la demande de permission doit suivre une action utilisateur,
 * ici le clic "Activer" du toast, pas son affichage). Respecte un délai de 7 jours après un rejet,
 * comme `AddToHomeScreenPopup`, et ne s'affiche jamais si la permission est déjà tranchée
 * (accordée ou refusée) ou le navigateur non éligible.
 */
export function PushEnablePrompt() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  const { support, permission, isRegistered, requestPermission } = usePushNotifications();
  const shownRef = useRef(false);

  useEffect(() => {
    if (shownRef.current) return;
    if (!isAuthenticated || !emailVerified) return;
    if (support !== "supported") return;
    if (permission !== "default") return;
    if (isRegistered) return;
    if (recentlyDismissed()) return;

    const timer = window.setTimeout(() => {
      shownRef.current = true;
      notify.custom(
        (t) => (
          <PromptToastBody
            toastId={t}
            onAccept={() => {
              notify.dismiss(t);
              void requestPermission();
            }}
          />
        ),
        { duration: 15_000 },
      );
    }, PROMPT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isAuthenticated, emailVerified, support, permission, isRegistered, requestPermission]);

  return null;
}
